/**
 * Typed API client for the TechSys Connect REST API.
 * All requests go through this client so MSW can intercept them in dev.
 */

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error: { code: string; message: string; details?: unknown };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: ErrorBody | null = null;
    try {
      body = await res.json();
    } catch {
      // ignore parse failure
    }
    const error = new ApiError(
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `HTTP ${res.status}`,
      body?.error?.details,
      res.status,
    );
    if (res.status === 403 && /multi-factor authentication/i.test(error.message) && typeof window !== 'undefined') {
      const returnTo = window.location.pathname + window.location.search;
      window.location.assign('/api/v1/auth/reauth?returnTo=' + encodeURIComponent(returnTo));
    }
    throw error;
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined | null>;
}

export function createApiClient(baseUrl = '/api/v1') {
  async function request<T>(
    path: string,
    { params, ...init }: RequestOptions = {},
  ): Promise<T> {
    let url = `${baseUrl}${path}`;
    if (params) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null) qs.set(k, String(v));
      }
      const str = qs.toString();
      if (str) url += `?${str}`;
    }

    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
      ...init,
    });
    return handleResponse<T>(res);
  }

  return {
    get: <T>(path: string, opts?: RequestOptions) =>
      request<T>(path, { method: 'GET', ...opts }),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body), ...opts }),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(body), ...opts }),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(path, { method: 'PATCH', body: JSON.stringify(body), ...opts }),
    delete: <T>(path: string, opts?: RequestOptions) =>
      request<T>(path, { method: 'DELETE', ...opts }),
  };
}

export const apiClient = createApiClient();
